import { create } from "zustand";
import { produce } from "immer";
import { employeeBelongsToCompany, unitBelongsToCompany } from "../lib/company";
import { ensureLayoutConfig, normalizeLayoutConfig } from "../lib/layout";
import { applyKnownHierarchyOverrides, applyManualParentOverrides, calculateHeadcounts, createEmptyState, periodKeyFor, reconcileDuplicateEmployeeRecords, reconcileKnownConcurrentManagerHomes } from "../lib/orgBuilder";
import { diffVersions } from "../lib/diff";
import { currentOrgPeriod } from "../lib/period";
import { getOrgChartSubtree } from "../lib/presentationLayout";
import { uid } from "../lib/id";
import { defaultVisibleMGradesForRoot, ensureViewConfig, normalizeViewConfig } from "../lib/viewConfig";
import type { AppMode, CompanyFilter, Employee, Language, LayoutConfig, OrgChartViewConfig, OrgState, OrgUnit, OrgVersionStatus, Snapshot, SnapshotScope, VersionDiff, VersionPair, VersionRecord } from "../types/org";

const LEGACY_LOCAL_DRAFT_KEY = "org-chart-local-draft-v1";
const LOCAL_DRAFT_PREFIX = "org-chart-local-draft-v2";
const LOCAL_HISTORY_KEY = "org-chart-version-history-v1";

interface OrgStore {
  mode: AppMode;
  state: OrgState;
  published?: OrgState;
  latestDraft?: OrgState;
  previousDraft?: OrgState;
  latestPublished?: OrgState;
  backups: VersionRecord[];
  previousPublished?: OrgState;
  undoStack: OrgState[];
  redoStack: OrgState[];
  lastDiff?: VersionDiff;
  dirty: boolean;
  loadingInitialData: boolean;
  setMode: (mode: AppMode) => void;
  loadPeriod: (year?: number, month?: number, mode?: AppMode) => Promise<void>;
  loadOfficialData: () => Promise<void>;
  setState: (state: OrgState, label?: string) => void;
  mutate: (label: string, recipe: (draft: OrgState) => void) => void;
  selectUnit: (unitId?: string) => void;
  focusUnit: (unitId: string) => void;
  selectEmployee: (employeeId?: string) => void;
  setCompanyFilter: (company: CompanyFilter) => void;
  setBusinessUnitFilter: (businessUnit: string) => void;
  setSearchQuery: (query: string) => void;
  applySearchMatch: (unitId?: string, employeeId?: string) => void;
  setLanguage: (language: Language) => void;
  setPeriod: (year: number, month: number) => void;
  copyPreviousPeriodToCurrent: () => Promise<boolean>;
  saveDraft: () => Promise<void>;
  publish: () => Promise<void>;
  getVersionDiff: (status?: Extract<OrgVersionStatus, "draft" | "published">) => VersionDiff;
  addSnapshot: (name: string, note?: string) => void;
  restoreSnapshot: (snapshotId: string) => void;
  deleteSnapshot: (snapshotId: string) => void;
  resolveIssue: (issueId: string, status: "resolved" | "ignored") => void;
  resolveAllOpenIssues: () => void;
  moveUnit: (unitId: string, position: { x: number; y: number }) => void;
  reparentUnit: (unitId: string, parentId: string) => boolean;
  setUnitParent: (unitId: string, parentId?: string) => boolean;
  moveEmployee: (employeeId: string, unitId: string) => void;
  updateUnit: (unitId: string, patch: Partial<OrgUnit>) => void;
  updateLayoutConfig: (patch: Partial<LayoutConfig>) => void;
  updateViewConfig: (patch: Partial<OrgChartViewConfig>) => void;
  updateUnitWithParent: (unitId: string, parentId: string | undefined, patch: Partial<OrgUnit>) => boolean;
  addUnit: (parentId?: string) => void;
  deleteUnit: (unitId: string) => boolean;
  updateEmployee: (employeeId: string, patch: Partial<Employee>) => void;
  addEmployee: (unitId?: string) => void;
  saveEmployee: (employeeId: string, unitId?: string) => boolean;
  deleteEmployee: (employeeId: string) => void;
  undo: () => void;
  redo: () => void;
}

export const useOrgStore = create<OrgStore>((set, get) => ({
  mode: "official",
  state: createEmptyState(),
  published: undefined,
  latestDraft: undefined,
  previousDraft: undefined,
  latestPublished: undefined,
  previousPublished: undefined,
  backups: [],
  undoStack: [],
  redoStack: [],
  dirty: false,
  loadingInitialData: false,

  setMode: (mode) => set({ mode }),

  loadPeriod: async (year, month, requestedMode) => {
    if (get().loadingInitialData) return;
    const mode = requestedMode ?? get().mode;
    set({ loadingInitialData: true, mode });
    try {
      const official = await loadOfficialState();
      const defaultPeriod = currentOrgPeriod();
      const periodYear = year ?? defaultPeriod.year;
      const periodMonth = month ?? defaultPeriod.month;
      const publishedPair = await loadVersionPair("published", periodYear, periodMonth, official);
      const draftPair = await loadVersionPair("draft", periodYear, periodMonth, official);
      const latestPublished = publishedPair.latest?.state ?? (official.year === periodYear && official.month === periodMonth ? official : createEmptyState(periodYear, periodMonth));
      const previousPublished = publishedPair.previous?.state;
      const latestDraft = draftPair.latest?.state;
      const previousDraft = draftPair.previous?.state;
      const activeState = mode === "draft" ? structuredClone(latestDraft ?? latestPublished) : structuredClone(latestPublished);
      activeState.year = periodYear;
      activeState.month = periodMonth;
      normalizeState(activeState, { persist: false });
      applyDefaultLaunchView(activeState);
      set({
        state: activeState,
        published: latestPublished,
        latestPublished,
        previousPublished,
        latestDraft,
        previousDraft,
        backups: [],
        lastDiff: mode === "draft" ? diffVersions(latestDraft ?? activeState, previousDraft) : diffVersions(latestPublished, previousPublished),
        undoStack: [],
        redoStack: [],
        dirty: false,
        loadingInitialData: false,
      });
    } catch (error) {
      console.error(error);
      set({ loadingInitialData: false });
    }
  },

  loadOfficialData: async () => {
    await get().loadPeriod(undefined, undefined, get().mode);
  },

  setState: (state, label = "載入草稿") =>
    {
      const nextState = structuredClone(state);
      normalizeState(nextState, { persist: false });
      if (get().mode === "draft") persistLocalDraft(nextState);
      set((current) => ({
        state: nextState,
        undoStack: [...current.undoStack, current.state].slice(-40),
        redoStack: [],
        dirty: current.mode === "draft",
        stateLabel: label,
      }));
    },

  mutate: (label, recipe) => {
    if (get().mode !== "draft") return;
    const before = get().state;
    const next = produce(before, (draft) => {
      recipe(draft);
      removeEmptyPlaceholderUnits(draft);
      draft.changeLog.push({ id: uid("change"), label, detail: label });
      calculateHeadcounts(draft);
    });
    persistLocalDraft(next);
    set((current) => ({
      state: next,
      undoStack: [...current.undoStack, before].slice(-40),
      redoStack: [],
      dirty: true,
    }));
  },

  selectUnit: (unitId) =>
    set((current) => ({
      state: { ...current.state, selectedUnitId: unitId, selectedEmployeeId: undefined },
    })),

  focusUnit: (unitId) =>
    set((current) => {
      const unit = current.state.units[unitId];
      const company = companyFromUnit(current.state, unit) ?? current.state.companyFilter;
      const companyChanged = company !== current.state.companyFilter;
      const currentViewConfig = normalizeViewConfig(current.state.viewConfig);
      const visibleMGrades = currentViewConfig.visibleMGradesByRootId?.[unitId]?.length
        ? currentViewConfig.visibleMGradesByRootId[unitId]
        : defaultVisibleMGradesForRoot(current.state, unitId);
      const scopedSelection = displayScopeSelectionForRoot(currentViewConfig, unitId);
      const viewConfig = unit ? normalizeViewConfig({
        ...currentViewConfig,
        rootUnitId: unitId,
        visibleMGrades,
        selectedUnitIds: scopedSelection.selectedUnitIds,
        hiddenUnitIds: scopedSelection.hiddenUnitIds,
        autoIncludeAncestors: true,
        hideDescendantsOfHiddenUnits: true,
        viewMode: "custom",
      }) : current.state.viewConfig;
      const nextState = {
        ...current.state,
        companyFilter: company,
        businessUnitFilter: companyChanged ? "" : current.state.businessUnitFilter,
        searchQuery: "",
        selectedUnitId: unitId,
        selectedEmployeeId: undefined,
        focusedUnitId: unitId,
        viewConfig,
      };
      if (current.mode === "draft") persistLocalDraft(nextState);
      return {
        state: nextState,
        dirty: current.mode === "draft",
      };
    }),

  selectEmployee: (employeeId) =>
    set((current) => ({
      state: { ...current.state, selectedEmployeeId: employeeId, selectedUnitId: undefined },
    })),

  setCompanyFilter: (company) =>
    set((current) => ({
      state: {
        ...current.state,
        companyFilter: company,
        businessUnitFilter: "",
        searchQuery: "",
        selectedUnitId: undefined,
        selectedEmployeeId: undefined,
        focusedUnitId: undefined,
        viewConfig: normalizeViewConfig({ ...current.state.viewConfig, rootUnitId: "", selectedUnitIds: [], hiddenUnitIds: [], viewMode: "full" }),
      },
    })),

  setBusinessUnitFilter: (businessUnit) =>
    set((current) => {
      const focusedUnitId = businessUnit ? businessUnitRootId(current.state, businessUnit) : undefined;
      const currentViewConfig = normalizeViewConfig(current.state.viewConfig);
      const scopedSelection = focusedUnitId ? displayScopeSelectionForRoot(currentViewConfig, focusedUnitId) : { selectedUnitIds: [], hiddenUnitIds: [] };
      return {
        state: {
          ...current.state,
          businessUnitFilter: businessUnit,
          searchQuery: "",
          selectedUnitId: focusedUnitId,
          selectedEmployeeId: undefined,
          focusedUnitId,
          viewConfig: normalizeViewConfig({
            ...currentViewConfig,
            rootUnitId: focusedUnitId ?? "",
            selectedUnitIds: scopedSelection.selectedUnitIds,
            hiddenUnitIds: scopedSelection.hiddenUnitIds,
            viewMode: focusedUnitId ? "custom" : "full",
            visibleMGrades: focusedUnitId
              ? currentViewConfig.visibleMGradesByRootId?.[focusedUnitId]?.length
                ? currentViewConfig.visibleMGradesByRootId[focusedUnitId]
                : defaultVisibleMGradesForRoot(current.state, focusedUnitId)
              : currentViewConfig.visibleMGrades,
          }),
        },
      };
    }),

  setSearchQuery: (query) =>
    set((current) => ({
      state: {
        ...current.state,
        searchQuery: query,
      },
    })),

  applySearchMatch: (unitId, employeeId) =>
    set((current) => {
      if (!unitId || !current.state.units[unitId]) return current;
      const currentViewConfig = normalizeViewConfig(current.state.viewConfig);
      const scopedSelection = displayScopeSelectionForRoot(currentViewConfig, unitId);
      return {
        state: {
          ...current.state,
          selectedUnitId: unitId,
          selectedEmployeeId: employeeId,
          focusedUnitId: unitId,
          viewConfig: normalizeViewConfig({
            ...currentViewConfig,
            rootUnitId: unitId,
            visibleMGrades: currentViewConfig.visibleMGradesByRootId?.[unitId]?.length
              ? currentViewConfig.visibleMGradesByRootId[unitId]
              : defaultVisibleMGradesForRoot(current.state, unitId),
            selectedUnitIds: scopedSelection.selectedUnitIds,
            hiddenUnitIds: scopedSelection.hiddenUnitIds,
            autoIncludeAncestors: true,
            hideDescendantsOfHiddenUnits: true,
            viewMode: "custom",
          }),
        },
      };
    }),

  setLanguage: (language) =>
    set((current) => ({
      state: { ...current.state, language },
    })),

  setPeriod: (year, month) => {
    void get().loadPeriod(year, month, get().mode);
  },

  copyPreviousPeriodToCurrent: async () => {
    const current = get();
    if (current.mode !== "draft") return false;

    const targetYear = current.state.year;
    const targetMonth = current.state.month;
    const sourcePeriod = previousPeriod(targetYear, targetMonth);
    const official = await loadOfficialState();
    const sourceDraftPair = await loadVersionPair("draft", sourcePeriod.year, sourcePeriod.month, official);
    const sourcePublishedPair = await loadVersionPair("published", sourcePeriod.year, sourcePeriod.month, official);
    const sourceState = sourceDraftPair.latest?.state ??
      sourcePublishedPair.latest?.state ??
      (official.year === sourcePeriod.year && official.month === sourcePeriod.month ? official : undefined);

    if (!sourceState || !Object.keys(sourceState.units ?? {}).length) return false;

    const nextState = structuredClone(sourceState);
    nextState.year = targetYear;
    nextState.month = targetMonth;
    nextState.periodKey = periodKeyFor(targetYear, targetMonth);
    nextState.revision = 1;
    nextState.canonicalSavedAt = undefined;
    nextState.snapshots = [];
    nextState.changeLog = [{
      id: uid("change"),
      label: "由前一月份複製建立",
      detail: `由 ${periodKeyFor(sourcePeriod.year, sourcePeriod.month)} 複製為 ${nextState.periodKey}`,
    }];
    applyDefaultLaunchView(nextState);
    normalizeState(nextState, { persist: false });

    const saved = await saveDraftToApi(nextState);
    nextState.revision = saved.revision ?? 1;
    nextState.canonicalSavedAt = saved.canonicalSavedAt ?? new Date().toISOString();
    persistLocalDraft(nextState);
    set((state) => ({
      state: nextState,
      latestDraft: nextState,
      previousDraft: undefined,
      lastDiff: diffVersions(nextState, undefined),
      undoStack: [...state.undoStack, state.state].slice(-40),
      redoStack: [],
      dirty: false,
    }));
    return true;
  },

  saveDraft: async () => {
    const { state, previousDraft } = get();
    const savedDraftState = structuredClone(state);
    normalizeState(savedDraftState, { persist: false });
    const saved = await saveDraftToApi(savedDraftState);
    savedDraftState.periodKey = periodKeyFor(savedDraftState.year, savedDraftState.month);
    savedDraftState.revision = saved.revision ?? ((get().latestDraft?.revision ?? savedDraftState.revision ?? 0) + 1);
    savedDraftState.canonicalSavedAt = saved.canonicalSavedAt ?? new Date().toISOString();
    const activeState = structuredClone(savedDraftState);
    restoreTransientViewState(activeState, state);
    persistLocalDraft(savedDraftState);
    set((current) => ({
      state: activeState,
      latestDraft: savedDraftState,
      previousDraft: current.latestDraft ?? previousDraft,
      lastDiff: diffVersions(savedDraftState, current.latestDraft ?? previousDraft),
      dirty: false,
    }));
  },

  publish: async () => {
    const { state, published, previousPublished } = get();
    const publishedState = structuredClone(state);
    normalizeState(publishedState, { persist: false });
    const lastDiff = diffVersions(publishedState, published);
    const saved = await publishToApi(publishedState);
    publishedState.revision = saved.revision ?? ((published?.revision ?? 0) + 1);
    publishedState.canonicalSavedAt = saved.canonicalSavedAt ?? new Date().toISOString();
    const activeState = structuredClone(publishedState);
    restoreTransientViewState(activeState, state);
    persistLocalDraft(publishedState);
    set({
      previousPublished: published ?? previousPublished,
      published: publishedState,
      latestPublished: publishedState,
      latestDraft: publishedState,
      previousDraft: get().latestDraft,
      backups: [],
      lastDiff,
      dirty: false,
      state: activeState,
    });
  },

  getVersionDiff: (status) => {
    const currentStatus = status ?? (get().mode === "draft" ? "draft" : "published");
    if (currentStatus === "draft") return diffVersions(get().latestDraft ?? get().state, get().previousDraft);
    return diffVersions(get().latestPublished ?? get().published ?? get().state, get().previousPublished);
  },

  addSnapshot: (name, note) => {
    const { state } = get();
    if (state.snapshots.length >= 20) return;
    const snapshotPayload = buildCurrentChartSnapshot(state);
    get().mutate("建立快照", (draft) => {
      draft.snapshots.unshift({
        id: uid("snapshot"),
        name,
        note,
        createdAt: new Date().toISOString(),
        ...snapshotPayload,
      });
    });
  },

  restoreSnapshot: (snapshotId) => {
    const snapshot = get().state.snapshots.find((item) => item.id === snapshotId);
    if (!snapshot) return;
    if (!snapshot.scope) {
      get().setState({ ...structuredClone(snapshot.state), snapshots: get().state.snapshots }, "還原快照");
      return;
    }
    get().mutate("還原快照", (draft) => {
      restoreScopedSnapshot(draft, snapshot);
    });
  },

  deleteSnapshot: (snapshotId) =>
    get().mutate("刪除快照", (draft) => {
      draft.snapshots = draft.snapshots.filter((snapshot) => snapshot.id !== snapshotId);
    }),

  resolveIssue: (issueId, status) =>
    get().mutate("更新待處理狀態", (draft) => {
      const issue = draft.issues.find((item) => item.id === issueId);
      if (issue) issue.status = status;
    }),

  resolveAllOpenIssues: () =>
    get().mutate("全部標記已確認", (draft) => {
      for (const issue of draft.issues) {
        if (issue.status === "open") issue.status = "resolved";
      }
    }),

  moveUnit: (unitId, position) =>
    get().mutate("移動單位位置", (draft) => {
      if (draft.units[unitId]) draft.units[unitId].position = position;
    }),

  reparentUnit: (unitId, parentId) => {
    const state = get().state;
    const unit = state.units[unitId];
    const parent = state.units[parentId];
    if (!unit || !parent) return false;
    const unitSource = unitSourceSystem(state, unit);
    const parentSource = unitSourceSystem(state, parent);
    if ((unitSource && parentSource && unitSource !== parentSource) || unitId === parentId || wouldCreateCycle(state, unitId, parentId)) return false;
    get().mutate("調整單位隸屬", (draft) => {
      draft.units[unitId].parentId = parentId;
      recordParentOverride(draft, unitId, parentId);
      draft.units[unitId].sourceSystem = parent.sourceSystem ?? draft.units[unitId].sourceSystem;
      draft.units[unitId].buBg = draft.units[unitId].buBg || parent.buBg;
      draft.units[unitId].isUnlinked = false;
      draft.unlinkedUnitIds = draft.unlinkedUnitIds.filter((id) => id !== unitId);
      draft.edges = draft.edges.filter((edge) => edge.target !== unitId);
      draft.edges.push({ id: uid("edge"), source: parentId, target: unitId, style: draft.units[unitId].lineStyle });
    });
    return true;
  },

  setUnitParent: (unitId, parentId) => {
    const state = get().state;
    const unit = state.units[unitId];
    if (!unit) return false;
    if (parentId) {
      const parent = state.units[parentId];
      const unitSource = unitSourceSystem(state, unit);
      const parentSource = parent ? unitSourceSystem(state, parent) : undefined;
      if (!parent || (unitSource && parentSource && unitSource !== parentSource) || unitId === parentId || wouldCreateCycle(state, unitId, parentId)) return false;
    }
    get().mutate("Update parent unit", (draft) => {
      const current = draft.units[unitId];
      if (!current) return;
      const parent = parentId ? draft.units[parentId] : undefined;
      current.parentId = parentId;
      recordParentOverride(draft, unitId, parentId);
      if (parent) {
        current.sourceSystem = parent.sourceSystem ?? current.sourceSystem;
        current.buBg = current.buBg || parent.buBg;
      }
      current.isUnlinked = false;
      draft.unlinkedUnitIds = draft.unlinkedUnitIds.filter((id) => id !== unitId);
      draft.edges = draft.edges.filter((edge) => edge.target !== unitId);
      if (parentId) draft.edges.push({ id: uid("edge"), source: parentId, target: unitId, style: current.lineStyle });
    });
    return true;
  },

  moveEmployee: (employeeId, unitId) =>
    get().mutate("員工轉調單位", (draft) => {
      const employee = draft.employees[employeeId];
      const unit = draft.units[unitId];
      if (!employee || !unit) return;
      employee.unitId = unitId;
      employee.draftUnitId = undefined;
      employee.departmentCode = unit.code;
      employee.departmentNameZh = unit.nameZh;
      employee.departmentNameEn = unit.nameEn;
      employee.managerEmployeeNo = unit.managerEmployeeNo;
      employee.managerName = unit.managerNameZh;
      employee.buBg = unit.buBg;
      employee.isDraft = false;
      draft.unassignedEmployeeIds = draft.unassignedEmployeeIds.filter((id) => id !== employeeId);
    }),

  updateUnit: (unitId, patch) =>
    get().mutate("編輯單位", (draft) => {
      const unit = draft.units[unitId];
      if (!unit) return;
      const { slidePosition, slideManualOffset, ...ordinaryPatch } = patch;
      Object.assign(unit, ordinaryPatch);
      const slideScopeId = normalizeViewConfig(draft.viewConfig).rootUnitId ?? "";
      if (Object.prototype.hasOwnProperty.call(patch, "slidePosition")) {
        const positions = { ...(unit.slidePositionByRootId ?? {}) };
        if (slidePosition) positions[slideScopeId] = structuredClone(slidePosition);
        else delete positions[slideScopeId];
        unit.slidePositionByRootId = Object.keys(positions).length ? positions : undefined;
        if (slideScopeId === "") unit.slidePosition = slidePosition ? structuredClone(slidePosition) : undefined;
      }
      if (Object.prototype.hasOwnProperty.call(patch, "slideManualOffset")) {
        const offsets = { ...(unit.slideManualOffsetByRootId ?? {}) };
        if (slideManualOffset) offsets[slideScopeId] = structuredClone(slideManualOffset);
        else delete offsets[slideScopeId];
        unit.slideManualOffsetByRootId = Object.keys(offsets).length ? offsets : undefined;
        if (slideScopeId === "") unit.slideManualOffset = slideManualOffset ? structuredClone(slideManualOffset) : undefined;
      }
    }),

  updateLayoutConfig: (patch) =>
    get().mutate("Update layout settings", (draft) => {
      draft.layoutConfig = normalizeLayoutConfig({ ...draft.layoutConfig, ...patch });
    }),

  updateViewConfig: (patch) =>
    set((current) => {
      const currentViewConfig = normalizeViewConfig(current.state.viewConfig);
      const rootUnitId = patch.rootUnitId ?? currentViewConfig.rootUnitId;
      const visibleMGrades = patch.visibleMGrades ?? currentViewConfig.visibleMGrades;
      const selectedUnitIds = patch.selectedUnitIds ?? currentViewConfig.selectedUnitIds;
      const hiddenUnitIds = patch.hiddenUnitIds ?? currentViewConfig.hiddenUnitIds;
      const nextState = {
        ...current.state,
        viewConfig: normalizeViewConfig({
          ...currentViewConfig,
          ...patch,
          selectedUnitIds,
          hiddenUnitIds,
          visibleMGradesByRootId: rootUnitId
            ? {
                ...currentViewConfig.visibleMGradesByRootId,
                [rootUnitId]: visibleMGrades,
              }
            : currentViewConfig.visibleMGradesByRootId,
          selectedUnitIdsByRootId: rootUnitId
            ? {
                ...currentViewConfig.selectedUnitIdsByRootId,
                [rootUnitId]: selectedUnitIds,
              }
            : currentViewConfig.selectedUnitIdsByRootId,
          hiddenUnitIdsByRootId: rootUnitId
            ? {
                ...currentViewConfig.hiddenUnitIdsByRootId,
                [rootUnitId]: hiddenUnitIds,
              }
            : currentViewConfig.hiddenUnitIdsByRootId,
        }),
      };
      if (current.mode === "draft") persistLocalDraft(nextState);
      return { state: nextState, dirty: current.mode === "draft" };
    }),

  updateUnitWithParent: (unitId, parentId, patch) => {
    const state = get().state;
    const unit = state.units[unitId];
    if (!unit) return false;
    if (parentId) {
      const parent = state.units[parentId];
      const unitSource = unitSourceSystem(state, unit);
      const parentSource = parent ? unitSourceSystem(state, parent) : undefined;
      if (!parent || (unitSource && parentSource && unitSource !== parentSource) || unitId === parentId || wouldCreateCycle(state, unitId, parentId)) return false;
    }
    get().mutate("儲存單位草稿", (draft) => {
      const current = draft.units[unitId];
      if (!current) return;
      const parent = parentId ? draft.units[parentId] : undefined;
      Object.assign(current, patch);
      current.parentId = parentId;
      recordParentOverride(draft, unitId, parentId);
      if (parent) {
        current.sourceSystem = parent.sourceSystem ?? current.sourceSystem;
        current.buBg = current.buBg || parent.buBg;
      }
      current.isUnlinked = false;
      draft.unlinkedUnitIds = draft.unlinkedUnitIds.filter((id) => id !== unitId);
      draft.edges = draft.edges.filter((edge) => edge.target !== unitId);
      if (parentId) draft.edges.push({ id: uid("edge"), source: parentId, target: unitId, style: current.lineStyle });
      for (const employee of Object.values(draft.employees)) {
        if (employee.unitId !== unitId) continue;
        employee.departmentCode = current.code;
        employee.departmentNameZh = current.nameZh;
        employee.departmentNameEn = current.nameEn;
        employee.managerEmployeeNo = current.managerEmployeeNo;
        employee.managerName = current.managerNameZh;
        employee.buBg = current.buBg;
      }
    });
    return true;
  },

  addUnit: (parentId) =>
    get().mutate("新增單位", (draft) => {
      const id = uid("unit");
      const parent = parentId ? draft.units[parentId] : undefined;
      const childCount = Object.values(draft.units).filter((unit) => unit.parentId === parentId).length;
      const teamIndex = childCount + 1;
      draft.units[id] = {
        id,
        code: parent?.code ?? "NEW",
        nameZh: parent ? `${parent.code} Team${teamIndex}` : "未命名單位",
        nameEn: parent ? `${parent.nameEn || parent.code} Team ${teamIndex}` : "Unnamed Unit",
        level: parent ? parent.level + 1 : 0,
        parentId,
        mGrade: "M5",
        managerNameZh: "未指定",
        lineStyle: parent?.lineStyle ?? "solid",
        position: parent ? { x: parent.position.x + 310, y: parent.position.y + 162 * Math.max(childCount, 0) } : { x: 120, y: 120 },
        expanded: false,
        staffExpanded: true,
        isDraft: true,
        headcount: 0,
        technicalHeadcount: 0,
        source: "manual",
        sourceSystem: parent?.sourceSystem,
        buBg: parent?.buBg,
        isUnlinked: !parentId,
      };
      if (parentId) draft.edges.push({ id: uid("edge"), source: parentId, target: id, style: "solid" });
      else draft.unlinkedUnitIds.push(id);
      if (draft.viewConfig?.viewMode === "custom" && parentId) {
        // An empty selection means "show every selectable unit". Adding a child
        // must not turn that scope into an explicit allow-list containing only
        // the newly-created unit, otherwise existing siblings disappear.
        const hasExplicitSelection = draft.viewConfig.selectedUnitIds.length > 0;
        if (hasExplicitSelection) {
          draft.viewConfig.selectedUnitIds = [...new Set([...draft.viewConfig.selectedUnitIds, id])];
        }
        draft.viewConfig.hiddenUnitIds = draft.viewConfig.hiddenUnitIds.filter((hiddenId) => hiddenId !== id);
        if (draft.viewConfig.rootUnitId) {
          draft.viewConfig.selectedUnitIdsByRootId = {
            ...(draft.viewConfig.selectedUnitIdsByRootId ?? {}),
            [draft.viewConfig.rootUnitId]: draft.viewConfig.selectedUnitIds,
          };
          draft.viewConfig.hiddenUnitIdsByRootId = {
            ...(draft.viewConfig.hiddenUnitIdsByRootId ?? {}),
            [draft.viewConfig.rootUnitId]: draft.viewConfig.hiddenUnitIds,
          };
        }
        if (!draft.viewConfig.visibleMGrades.includes("M5")) {
          draft.viewConfig.visibleMGrades = [...draft.viewConfig.visibleMGrades, "M5"];
          if (draft.viewConfig.rootUnitId) {
            draft.viewConfig.visibleMGradesByRootId = {
              ...(draft.viewConfig.visibleMGradesByRootId ?? {}),
              [draft.viewConfig.rootUnitId]: draft.viewConfig.visibleMGrades,
            };
          }
        }
      }
      draft.selectedUnitId = id;
    }),

  deleteUnit: (unitId) => {
    const state = get().state;
    const hasChildren = Object.values(state.units).some((unit) => unit.parentId === unitId);
    const hasEmployees = Object.values(state.employees).some((employee) => employee.unitId === unitId);
    if (hasChildren || hasEmployees) return false;
    get().mutate("刪除單位", (draft) => {
      delete draft.units[unitId];
      draft.edges = draft.edges.filter((edge) => edge.source !== unitId && edge.target !== unitId);
      draft.dashedEdges = draft.dashedEdges.filter((edge) => edge.source !== unitId && edge.target !== unitId);
    });
    return true;
  },

  updateEmployee: (employeeId, patch) =>
    get().mutate("編輯員工", (draft) => {
      const employee = draft.employees[employeeId];
      if (!employee) return;
      const previousEmployeeNo = employee.employeeNo;
      Object.assign(employee, patch);
      syncManagerReferences(draft, previousEmployeeNo, employee);
    }),

  addEmployee: (unitId) =>
    get().mutate("新增員工", (draft) => {
      const id = uid("emp");
      const unit = unitId ? draft.units[unitId] : undefined;
      draft.employees[id] = {
        id,
        employeeNo: "NEW",
        chineseName: "新增員工",
        englishName: "New Employee",
        draftUnitId: unit?.id,
        departmentCode: unit?.code,
        departmentNameZh: unit?.nameZh,
        departmentNameEn: unit?.nameEn,
        managerEmployeeNo: unit?.managerEmployeeNo,
        managerName: unit?.managerNameZh,
        buBg: unit?.buBg,
        pGrade: "",
        source: "manual",
        isDraft: true,
      };
      if (!unit) draft.unassignedEmployeeIds.push(id);
      draft.selectedUnitId = undefined;
      draft.selectedEmployeeId = id;
    }),

  saveEmployee: (employeeId, unitId) => {
    const state = get().state;
    const employee = state.employees[employeeId];
    const targetUnitId = unitId || employee?.draftUnitId || employee?.unitId;
    if (!employee || !targetUnitId || !state.units[targetUnitId]) return false;
    get().mutate("儲存員工", (draft) => {
      const current = draft.employees[employeeId];
      const unit = draft.units[targetUnitId];
      if (!current || !unit) return;
      current.unitId = unit.id;
      current.draftUnitId = undefined;
      current.departmentCode = unit.code;
      current.departmentNameZh = unit.nameZh;
      current.departmentNameEn = unit.nameEn;
      current.managerEmployeeNo = current.managerEmployeeNo || unit.managerEmployeeNo;
      current.managerName = current.managerName || unit.managerNameZh;
      current.buBg = current.buBg || unit.buBg;
      current.isDraft = false;
      draft.unassignedEmployeeIds = draft.unassignedEmployeeIds.filter((id) => id !== employeeId);
    });
    return true;
  },

  deleteEmployee: (employeeId) =>
    get().mutate("刪除員工", (draft) => {
      delete draft.employees[employeeId];
      draft.unassignedEmployeeIds = draft.unassignedEmployeeIds.filter((id) => id !== employeeId);
    }),

  undo: () => {
    const { undoStack, state, redoStack } = get();
    const previous = undoStack.at(-1);
    if (!previous) return;
    persistLocalDraft(previous);
    set({
      state: previous,
      undoStack: undoStack.slice(0, -1),
      redoStack: [...redoStack, state].slice(-40),
      dirty: true,
    });
  },

  redo: () => {
    const { redoStack, state, undoStack } = get();
    const next = redoStack.at(-1);
    if (!next) return;
    persistLocalDraft(next);
    set({
      state: next,
      redoStack: redoStack.slice(0, -1),
      undoStack: [...undoStack, state].slice(-40),
      dirty: true,
    });
  },
}));

function wouldCreateCycle(state: OrgState, unitId: string, nextParentId: string): boolean {
  let current: string | undefined = nextParentId;
  while (current) {
    if (current === unitId) return true;
    current = state.units[current]?.parentId;
  }
  return false;
}

function unitSourceSystem(state: OrgState, unit?: OrgUnit): string | undefined {
  let current = unit;
  while (current) {
    if (current.sourceSystem) return current.sourceSystem;
    current = current.parentId ? state.units[current.parentId] : undefined;
  }
  return undefined;
}

function removeEmptyPlaceholderUnits(state: OrgState) {
  let removed = 0;
  let changed = true;
  while (changed) {
    changed = false;
    const childIds = new Set(Object.values(state.units).map((unit) => unit.parentId).filter(Boolean));
    const employeeUnitIds = new Set(Object.values(state.employees).map((employee) => employee.unitId).filter(Boolean));
    for (const unit of Object.values(state.units)) {
      const isOldPlaceholder = unit.source === "manual" && unit.code === "NEW" && unit.nameZh === "新增單位";
      if (!isOldPlaceholder || childIds.has(unit.id) || employeeUnitIds.has(unit.id)) continue;
      delete state.units[unit.id];
      state.edges = state.edges.filter((edge) => edge.source !== unit.id && edge.target !== unit.id);
      state.dashedEdges = state.dashedEdges.filter((edge) => edge.source !== unit.id && edge.target !== unit.id);
      state.unlinkedUnitIds = state.unlinkedUnitIds.filter((id) => id !== unit.id);
      if (state.selectedUnitId === unit.id) state.selectedUnitId = undefined;
      if (state.focusedUnitId === unit.id) state.focusedUnitId = undefined;
      removed += 1;
      changed = true;
    }
  }
  return removed;
}

function companyFromSource(source?: string): CompanyFilter | undefined {
  const normalized = String(source ?? "").toLowerCase();
  if (normalized === "benq") return "BenQ";
  if (normalized === "qisda") return "Qisda";
  return undefined;
}

function companyFromUnit(state: OrgState, unit?: OrgUnit): CompanyFilter | undefined {
  if (unitBelongsToCompany(state, unit, "BQP")) return "BQP";
  return companyFromSource(unitSourceSystem(state, unit));
}

function businessUnitRootId(state: OrgState, businessUnit: string): string | undefined {
  const company = state.companyFilter ?? "Qisda";
  const units = Object.values(state.units).filter((unit) => !unit.isUnlinked && unitBelongsToCompany(state, unit, company));
  const directMatches = new Set<string>();
  const departmentCodes = new Set<string>();

  for (const unit of units) {
    if (matchesBusinessUnit(unit, businessUnit)) directMatches.add(unit.id);
  }

  for (const employee of Object.values(state.employees)) {
    if (!employeeBelongsToCompany(state, employee, company) || !sameToken(employee.buBg, businessUnit)) continue;
    if (employee.unitId) directMatches.add(employee.unitId);
    if (employee.departmentCode) departmentCodes.add(employee.departmentCode);
  }

  for (const unit of units) {
    if (departmentCodes.has(unit.code)) directMatches.add(unit.id);
  }

  const matchedUnits = units.filter((unit) => directMatches.has(unit.id));
  const candidates = matchedUnits.filter((unit) => !isCompanyOverviewRoot(company, unit) || matchedUnits.length === 1);
  const candidateSet = new Set(candidates.map((unit) => unit.id));
  return candidates
    .filter((unit) => !hasAncestorInSet(state, unit, candidateSet))
    .sort(compareBusinessRoot)[0]?.id ?? matchedUnits.sort(compareBusinessRoot)[0]?.id;
}

function matchesBusinessUnit(unit: OrgUnit, businessUnit: string) {
  return sameToken(unit.buBg, businessUnit) ||
    sameToken(unit.code, businessUnit) ||
    includesToken(unit.nameZh, businessUnit) ||
    includesToken(unit.nameEn, businessUnit);
}

function isCompanyOverviewRoot(company: CompanyFilter, unit: OrgUnit) {
  const code = unit.code.trim().toUpperCase();
  if (company === "BenQ") return code === "B000";
  if (company === "Qisda") return code === "C000" || code === "E000";
  return false;
}

function hasAncestorInSet(state: OrgState, unit: OrgUnit, ids: Set<string>) {
  let current = unit.parentId ? state.units[unit.parentId] : undefined;
  while (current) {
    if (ids.has(current.id)) return true;
    current = current.parentId ? state.units[current.parentId] : undefined;
  }
  return false;
}

function compareBusinessRoot(a: OrgUnit, b: OrgUnit) {
  return a.level - b.level ||
    mGradeRank(b.mGrade) - mGradeRank(a.mGrade) ||
    a.code.localeCompare(b.code, undefined, { numeric: true }) ||
    a.nameZh.localeCompare(b.nameZh);
}

function sameToken(value: unknown, token: string) {
  return String(value ?? "").trim().toLowerCase() === token.trim().toLowerCase();
}

function includesToken(value: unknown, token: string) {
  return String(value ?? "").toLowerCase().includes(token.trim().toLowerCase());
}

function normalizeState(state: OrgState, options: { persist?: boolean } = {}): OrgState {
  state.periodKey = periodKeyFor(state.year, state.month);
  const removedPlaceholders = removeEmptyPlaceholderUnits(state);
  const hierarchyChanged = applyKnownHierarchyOverrides(state);
  const manualHierarchyChanged = applyManualParentOverrides(state);
  const employeeRecordsChanged = reconcileDuplicateEmployeeRecords(state);
  const employeeHomesChanged = reconcileKnownConcurrentManagerHomes(state);
  state.language = state.language ?? "zh";
  ensureLayoutConfig(state);
  state.companyFilter = state.companyFilter ?? "Qisda";
  state.businessUnitFilter = state.businessUnitFilter ?? "";
  state.searchQuery = state.searchQuery ?? "";
  state.selectedUnitId = undefined;
  state.selectedEmployeeId = undefined;
  state.focusedUnitId = undefined;
  ensureViewConfig(state);
  calculateHeadcounts(state);
  if ((options.persist ?? true) && (removedPlaceholders || hierarchyChanged || manualHierarchyChanged || employeeRecordsChanged || employeeHomesChanged)) persistLocalDraft(state);
  return state;
}

function restoreTransientViewState(target: OrgState, source: OrgState) {
  target.companyFilter = source.companyFilter;
  target.businessUnitFilter = source.businessUnitFilter;
  target.searchQuery = source.searchQuery;
  target.selectedUnitId = source.selectedUnitId;
  target.selectedEmployeeId = source.selectedEmployeeId;
  target.focusedUnitId = source.focusedUnitId;
  target.viewConfig = structuredClone(source.viewConfig);
  ensureViewConfig(target);
}

function displayScopeSelectionForRoot(config: OrgChartViewConfig, rootUnitId: string) {
  return {
    selectedUnitIds: config.selectedUnitIdsByRootId?.[rootUnitId] ?? [],
    hiddenUnitIds: config.hiddenUnitIdsByRootId?.[rootUnitId] ?? [],
  };
}

function applyDefaultLaunchView(state: OrgState) {
  state.companyFilter = "Qisda";
  state.businessUnitFilter = "";
  state.searchQuery = "";
  state.selectedUnitId = undefined;
  state.selectedEmployeeId = undefined;
  state.focusedUnitId = undefined;
  state.viewConfig = normalizeViewConfig({
    ...state.viewConfig,
    rootUnitId: "",
    selectedUnitIds: [],
    hiddenUnitIds: [],
    viewMode: "full",
  });
}

function previousPeriod(year: number, month: number) {
  if (month > 1) return { year, month: month - 1 };
  return { year: year - 1, month: 12 };
}

function buildCurrentChartSnapshot(state: OrgState): Pick<Snapshot, "state" | "scope"> {
  const subtree = getOrgChartSubtree(state, state.selectedUnitId ?? state.focusedUnitId);
  const unitIds = new Set(subtree.visibleUnits.map((unit) => unit.id));
  const employeeIds = new Set(subtree.visibleEmployees.map((employee) => employee.id));
  const employeeByNo = new Map(Object.values(state.employees).filter((employee) => employee.employeeNo).map((employee) => [employee.employeeNo, employee]));

  for (const unit of subtree.visibleUnits) {
    const manager = unit.managerEmployeeNo ? employeeByNo.get(unit.managerEmployeeNo) : undefined;
    const secretary = unit.secretaryEmployeeNo ? employeeByNo.get(unit.secretaryEmployeeNo) : undefined;
    if (manager) employeeIds.add(manager.id);
    if (secretary) employeeIds.add(secretary.id);
  }

  const edgeIds = new Set(subtree.visibleReportingLines.map((edge) => edge.id));
  const dashedEdgeIds = new Set(subtree.visibleDashedReportingLines.map((edge) => edge.id));
  const scopedState = structuredClone(state);
  scopedState.units = Object.fromEntries([...unitIds].map((id) => [id, structuredClone(state.units[id])]).filter(([, unit]) => Boolean(unit))) as OrgState["units"];
  scopedState.employees = Object.fromEntries([...employeeIds].map((id) => [id, structuredClone(state.employees[id])]).filter(([, employee]) => Boolean(employee))) as OrgState["employees"];
  scopedState.edges = state.edges.filter((edge) => edgeIds.has(edge.id)).map((edge) => structuredClone(edge));
  scopedState.dashedEdges = state.dashedEdges.filter((edge) => dashedEdgeIds.has(edge.id)).map((edge) => structuredClone(edge));
  scopedState.manualParentOverrides = Object.fromEntries(Object.entries(state.manualParentOverrides ?? {}).filter(([unitId]) => unitIds.has(unitId)));
  scopedState.unassignedEmployeeIds = state.unassignedEmployeeIds.filter((id) => employeeIds.has(id));
  scopedState.unlinkedUnitIds = state.unlinkedUnitIds.filter((id) => unitIds.has(id));
  scopedState.issues = state.issues.filter((issue) => issueInSnapshotScope(issue, unitIds, employeeIds, state));
  scopedState.snapshots = [];
  scopedState.changeLog = [];

  const scope: SnapshotScope = {
    rootUnitId: subtree.rootUnit?.id,
    unitIds: [...unitIds],
    employeeIds: [...employeeIds],
    edgeIds: [...edgeIds],
    dashedEdgeIds: [...dashedEdgeIds],
  };
  return { state: scopedState, scope };
}

function restoreScopedSnapshot(draft: OrgState, snapshot: Snapshot) {
  if (!snapshot.scope) return;
  const snapshotState = structuredClone(snapshot.state);
  const restoreViewState = {
    ...draft,
    companyFilter: snapshotState.companyFilter,
    businessUnitFilter: snapshotState.businessUnitFilter,
    focusedUnitId: snapshotState.focusedUnitId,
    selectedUnitId: snapshotState.selectedUnitId,
    selectedEmployeeId: undefined,
    viewConfig: snapshotState.viewConfig,
  };
  const currentSubtree = getOrgChartSubtree(restoreViewState, restoreViewState.selectedUnitId ?? restoreViewState.focusedUnitId);
  const currentPageUnitIds = new Set(currentSubtree.visibleUnits.map((unit) => unit.id));
  const savedUnitIds = new Set(snapshot.scope.unitIds);
  const savedEmployeeIds = new Set(snapshot.scope.employeeIds);

  for (const id of currentPageUnitIds) {
    if (!savedUnitIds.has(id)) delete draft.units[id];
  }
  for (const id of savedUnitIds) {
    const unit = snapshotState.units[id];
    if (unit) draft.units[id] = structuredClone(unit);
  }

  const currentPageEmployeeIds = new Set(currentSubtree.visibleEmployees.map((employee) => employee.id));
  for (const employee of Object.values(draft.employees)) {
    if (employee.unitId && currentPageUnitIds.has(employee.unitId)) currentPageEmployeeIds.add(employee.id);
    if (employee.draftUnitId && currentPageUnitIds.has(employee.draftUnitId)) currentPageEmployeeIds.add(employee.id);
  }
  for (const id of currentPageEmployeeIds) {
    if (!savedEmployeeIds.has(id)) delete draft.employees[id];
  }
  for (const id of savedEmployeeIds) {
    const employee = snapshotState.employees[id];
    if (employee) draft.employees[id] = structuredClone(employee);
  }

  draft.edges = [
    ...draft.edges.filter((edge) => !currentPageUnitIds.has(edge.source) && !currentPageUnitIds.has(edge.target)),
    ...snapshotState.edges.map((edge) => structuredClone(edge)),
  ];
  draft.dashedEdges = [
    ...draft.dashedEdges.filter((edge) => !currentPageUnitIds.has(edge.source) && !currentPageUnitIds.has(edge.target)),
    ...snapshotState.dashedEdges.map((edge) => structuredClone(edge)),
  ];

  const manualParentOverrides = { ...(draft.manualParentOverrides ?? {}) };
  for (const id of currentPageUnitIds) delete manualParentOverrides[id];
  for (const [id, parentId] of Object.entries(snapshotState.manualParentOverrides ?? {})) {
    if (savedUnitIds.has(id)) manualParentOverrides[id] = parentId;
  }
  draft.manualParentOverrides = manualParentOverrides;
  draft.unassignedEmployeeIds = [
    ...draft.unassignedEmployeeIds.filter((id) => !currentPageEmployeeIds.has(id)),
    ...snapshotState.unassignedEmployeeIds,
  ];
  draft.unlinkedUnitIds = [
    ...draft.unlinkedUnitIds.filter((id) => !currentPageUnitIds.has(id)),
    ...snapshotState.unlinkedUnitIds,
  ];
  draft.issues = [
    ...draft.issues.filter((issue) => !issueInSnapshotScope(issue, currentPageUnitIds, currentPageEmployeeIds, draft)),
    ...snapshotState.issues.map((issue) => structuredClone(issue)),
  ];
  draft.companyFilter = snapshotState.companyFilter;
  draft.businessUnitFilter = snapshotState.businessUnitFilter;
  draft.focusedUnitId = snapshotState.focusedUnitId;
  draft.selectedUnitId = snapshotState.selectedUnitId;
  draft.selectedEmployeeId = snapshotState.selectedEmployeeId;
  draft.viewConfig = structuredClone(snapshotState.viewConfig);
  draft.layoutConfig = structuredClone(snapshotState.layoutConfig);
}

function issueInSnapshotScope(issue: OrgState["issues"][number], unitIds: Set<string>, employeeIds: Set<string>, state: OrgState) {
  if (issue.unitId && unitIds.has(issue.unitId)) return true;
  if (!issue.employeeNo) return false;
  return [...employeeIds].some((id) => state.employees[id]?.employeeNo === issue.employeeNo);
}

function recordParentOverride(state: OrgState, unitId: string, parentId?: string) {
  state.manualParentOverrides = {
    ...(state.manualParentOverrides ?? {}),
    [unitId]: parentId ?? null,
  };
}

function syncManagerReferences(state: OrgState, previousEmployeeNo: string, manager: Employee) {
  const managerKeys = new Set([previousEmployeeNo, manager.employeeNo].filter(Boolean));
  if (!managerKeys.size) return;

  for (const unit of Object.values(state.units)) {
    if (!unit.managerEmployeeNo || !managerKeys.has(unit.managerEmployeeNo)) continue;
    unit.managerEmployeeNo = manager.employeeNo;
    unit.managerNameZh = manager.chineseName;
    unit.managerNameEn = manager.englishName;
  }

  for (const employee of Object.values(state.employees)) {
    if (employee.id === manager.id || !employee.managerEmployeeNo || !managerKeys.has(employee.managerEmployeeNo)) continue;
    employee.managerEmployeeNo = manager.employeeNo;
    employee.managerName = manager.chineseName;
  }
}

function normalizeImportedHierarchy(state: OrgState) {
  const importedGroups = new Map<string, OrgUnit[]>();
  for (const unit of Object.values(state.units)) {
    if (unit.source !== "imported" || !unit.sourceSystem || unit.isUnlinked) continue;
    importedGroups.set(unit.sourceSystem, [...(importedGroups.get(unit.sourceSystem) ?? []), unit]);
  }

  let changed = false;
  for (const units of importedGroups.values()) {
    const stack = new Map<number, OrgUnit>();
    for (const unit of [...units].sort(compareImportedOrder)) {
      pruneHierarchyStack(stack, unit.level);
      const parentId = findHierarchyParent(stack, unit.level, unit.mGrade);
      if (unit.parentId !== parentId) {
        unit.parentId = parentId;
        changed = true;
      }
      stack.set(unit.level, unit);
    }
  }

  if (!changed) return;
  const existingEdges = new Map(state.edges.map((edge) => [`${edge.source}->${edge.target}`, edge]));
  state.edges = Object.values(state.units)
    .filter((unit) => unit.parentId && !unit.isUnlinked)
    .sort(compareImportedOrder)
    .map((unit) => existingEdges.get(`${unit.parentId}->${unit.id}`) ?? {
      id: uid("edge"),
      source: unit.parentId!,
      target: unit.id,
      style: unit.lineStyle,
    });
}

function reconcileImportedHierarchy(draft: OrgState, official: OrgState) {
  let changed = false;
  for (const officialUnit of Object.values(official.units)) {
    const draftUnit = draft.units[officialUnit.id];
    if (!draftUnit || draftUnit.source !== "imported" || officialUnit.source !== "imported") continue;
    if (
      draftUnit.level !== officialUnit.level ||
      draftUnit.sourceSystem !== officialUnit.sourceSystem
    ) {
      draftUnit.level = officialUnit.level;
      draftUnit.sourceSystem = officialUnit.sourceSystem;
      changed = true;
    }
  }
  if (!changed) return;
  calculateHeadcounts(draft);
  persistLocalDraft(draft);
}

function pruneHierarchyStack(stack: Map<number, OrgUnit>, level: number) {
  for (const key of [...stack.keys()]) {
    if (key >= level) stack.delete(key);
  }
}

function findHierarchyParent(stack: Map<number, OrgUnit>, level: number, childMGrade = "") {
  const childRank = mGradeRank(childMGrade);
  for (let target = level - 1; target >= 0; target -= 1) {
    const parent = stack.get(target);
    if (!parent) continue;
    if (childRank) {
      const parentRank = mGradeRank(parent.mGrade);
      if (!parentRank || parentRank <= childRank) continue;
    }
    return parent.id;
  }
  return undefined;
}

function mGradeRank(value?: string) {
  const match = String(value ?? "").trim().match(/^M\s*(\d+)$/i);
  return match ? Number(match[1]) : 0;
}

function compareImportedOrder(a: OrgUnit, b: OrgUnit) {
  return sourceOrder(a) - sourceOrder(b) || a.code.localeCompare(b.code, undefined, { numeric: true }) || a.level - b.level || a.nameZh.localeCompare(b.nameZh);
}

function sourceOrder(unit: OrgUnit) {
  const match = unit.id.match(/^unit_[^_]+_(\d+)_/);
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
}

interface LocalDraftPayload {
  state: OrgState;
  savedAt?: string;
}

function loadLocalDraft(year?: number, month?: number): LocalDraftPayload | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const periodRaw = year && month ? window.localStorage.getItem(localDraftKey(year, month)) : undefined;
    const periodDraft = parseLocalDraft(periodRaw);
    if (periodDraft) return periodDraft;

    const legacyDraft = parseLocalDraft(window.localStorage.getItem(LEGACY_LOCAL_DRAFT_KEY));
    if (!legacyDraft) return undefined;
    if (year && month && (legacyDraft.state.year !== year || legacyDraft.state.month !== month)) return undefined;
    return legacyDraft;
  } catch (error) {
    console.warn("Unable to load local org draft", error);
    return undefined;
  }
}

function parseLocalDraft(raw?: string | null): LocalDraftPayload | undefined {
  if (!raw) return undefined;
  const payload = JSON.parse(raw) as { state?: OrgState; savedAt?: string };
  return payload.state ? { state: normalizeState(payload.state, { persist: false }), savedAt: payload.savedAt } : undefined;
}

function shouldUseLocalDraft(localDraft: LocalDraftPayload, official: OrgState) {
  const local = localDraft.state;
  if (local.year !== official.year || local.month !== official.month) return false;
  if ((local.revision ?? 0) > (official.revision ?? 0)) return true;
  if ((local.revision ?? 0) < (official.revision ?? 0)) return false;
  const localSavedAt = Date.parse(localDraft.savedAt ?? "");
  const officialSavedAt = Date.parse(official.canonicalSavedAt ?? "");
  if (official.canonicalSavedAt && local.canonicalSavedAt !== official.canonicalSavedAt) {
    if (Number.isFinite(localSavedAt) && Number.isFinite(officialSavedAt)) return localSavedAt >= officialSavedAt;
    return false;
  }
  if (Number.isFinite(localSavedAt) && Number.isFinite(officialSavedAt)) return localSavedAt >= officialSavedAt;
  return true;
}

async function loadOfficialState() {
  const response = await fetch("/data/official-state.json", { cache: "no-store" });
  if (!response.ok) throw new Error(`Unable to load official data: ${response.status}`);
  return normalizeState((await response.json()) as OrgState, { persist: false });
}

async function loadVersionPair(status: Extract<OrgVersionStatus, "draft" | "published">, year: number, month: number, official: OrgState): Promise<VersionPair> {
  const localPair = loadLocalVersionPair(status, year, month);
  const localDraftRecord = status === "draft" ? localDraftVersionRecord(year, month, official) : undefined;
  try {
    const response = await fetch(`/api/org/versions?year=${year}&month=${month}&status=${status}`, { cache: "no-store" });
    if (!response.ok) throw new Error(`Version API unavailable: ${response.status}`);
    const payload = await response.json() as VersionPair | VersionRecord[] | Array<Record<string, unknown>>;
    const apiPair = normalizeVersionPairPayload(payload);
    const resolvedPair = latestLocalDraftFirst(apiPair, localDraftRecord);
    if (resolvedPair.latest || resolvedPair.previous) return resolvedPair;
  } catch {
    // Vite dev does not serve Pages Functions; local history is the expected fallback.
  }

  const resolvedLocalPair = latestLocalDraftFirst(localPair, localDraftRecord);
  if (resolvedLocalPair.latest || resolvedLocalPair.previous) return resolvedLocalPair;
  if (status === "published" && official.year === year && official.month === month) {
    return { latest: versionRecordFromState("published", official, official.canonicalSavedAt) };
  }
  return {};
}

function localDraftVersionRecord(year: number, month: number, official: OrgState) {
  const localDraft = loadLocalDraft(year, month);
  if (!localDraft?.state || localDraft.state.year !== year || localDraft.state.month !== month || !shouldUseLocalDraft(localDraft, official)) return undefined;
  return versionRecordFromState("draft", localDraft.state, localDraft.savedAt);
}

function latestLocalDraftFirst(pair: VersionPair, localDraft?: VersionRecord): VersionPair {
  if (!localDraft) return pair;
  const latestTime = Date.parse(pair.latest?.createdAt ?? "");
  const draftTime = Date.parse(localDraft.createdAt);
  if (pair.latest && Number.isFinite(latestTime) && Number.isFinite(draftTime) && latestTime > draftTime) return pair;
  return {
    latest: localDraft,
    previous: pair.latest && pair.latest.id !== localDraft.id ? pair.latest : pair.previous,
  };
}

function normalizeVersionPairPayload(payload: VersionPair | VersionRecord[] | Array<Record<string, unknown>>): VersionPair {
  if (!Array.isArray(payload)) {
    return {
      latest: payload.latest ? normalizeVersionRecord(payload.latest) : undefined,
      previous: payload.previous ? normalizeVersionRecord(payload.previous) : undefined,
    };
  }
  const records = payload.map((item) => normalizeVersionRecord(item as VersionRecord)).filter(Boolean) as VersionRecord[];
  return {
    latest: records[0],
    previous: records[1],
  };
}

function normalizeVersionRecord(record: VersionRecord | Record<string, unknown>): VersionRecord | undefined {
  const rawState = "state" in record ? record.state : "data" in record && typeof record.data === "string" ? JSON.parse(record.data) : undefined;
  if (!rawState) return undefined;
  const state = normalizeState(rawState as OrgState, { persist: false });
  const source = record as Record<string, unknown>;
  return {
    id: String(record.id ?? uid("version")),
    status: (record.status as OrgVersionStatus) ?? "draft",
    label: String(record.label ?? `${state.year}/${state.month}`),
    createdAt: String(source.createdAt ?? source.created_at ?? source.updatedAt ?? source.updated_at ?? state.canonicalSavedAt ?? new Date().toISOString()),
    revision: Number(record.revision ?? state.revision ?? 1),
    state,
  };
}

function loadLocalVersionPair(status: Extract<OrgVersionStatus, "draft" | "published">, year: number, month: number): VersionPair {
  if (typeof window === "undefined") return {};
  try {
    const records = JSON.parse(window.localStorage.getItem(LOCAL_HISTORY_KEY) ?? "[]") as VersionRecord[];
    const matches = records
      .map((record) => normalizeVersionRecord(record))
      .filter((record): record is VersionRecord => {
        if (!record) return false;
        return record.status === status && record.state.year === year && record.state.month === month;
      })
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
    return { latest: matches[0], previous: matches[1] };
  } catch {
    return {};
  }
}

function saveLocalVersion(status: Extract<OrgVersionStatus, "draft" | "published">, state: OrgState): { record: VersionRecord; persisted: boolean } {
  const createdAt = new Date().toISOString();
  const normalizedState = structuredClone(state);
  normalizeState(normalizedState, { persist: false });
  const pair = loadLocalVersionPair(status, normalizedState.year, normalizedState.month);
  const revision = (pair.latest?.revision ?? 0) + 1;
  const nextState = { ...normalizedState, periodKey: periodKeyFor(normalizedState.year, normalizedState.month), revision, canonicalSavedAt: createdAt };
  const record = versionRecordFromState(status, nextState, createdAt);
  let persisted = false;
  if (typeof window !== "undefined") {
    try {
      const records = JSON.parse(window.localStorage.getItem(LOCAL_HISTORY_KEY) ?? "[]") as VersionRecord[];
      const others = records.filter((item) => !(item.status === status && item.state?.year === normalizedState.year && item.state?.month === normalizedState.month));
      const keep = [record, ...(pair.latest ? [pair.latest] : [])].slice(0, 2);
      window.localStorage.setItem(LOCAL_HISTORY_KEY, JSON.stringify([...keep, ...others]));
      persisted = true;
    } catch (error) {
      console.warn("Unable to persist local org version history; keeping latest version only", error);
      try {
        window.localStorage.setItem(LOCAL_HISTORY_KEY, JSON.stringify([record]));
        persisted = true;
      } catch (fallbackError) {
        console.warn("Unable to persist local org version history", fallbackError);
      }
    }
  }
  return { record, persisted };
}

function versionRecordFromState(status: Extract<OrgVersionStatus, "draft" | "published">, state: OrgState, createdAt = new Date().toISOString()): VersionRecord {
  return {
    id: `${status}-${state.year}-${state.month}-${Date.parse(createdAt) || Date.now()}`,
    status,
    label: `${state.year}/${state.month} ${status}`,
    createdAt,
    revision: state.revision ?? 1,
    state: structuredClone(state),
  };
}

function persistLocalDraft(state: OrgState) {
  if (typeof window === "undefined") return false;
  try {
    const normalizedState = structuredClone(state);
    normalizeState(normalizedState, { persist: false });
    const payload = JSON.stringify({
      version: 1,
      periodKey: normalizedState.periodKey,
      savedAt: new Date().toISOString(),
      state: normalizedState,
    });
    window.localStorage.removeItem(LEGACY_LOCAL_DRAFT_KEY);
    window.localStorage.setItem(localDraftKey(normalizedState.year, normalizedState.month), payload);
    return true;
  } catch (error) {
    console.warn("Unable to persist local org draft", error);
    return false;
  }
}

function localDraftKey(year: number, month: number) {
  return `${LOCAL_DRAFT_PREFIX}:${periodKeyFor(year, month)}`;
}

async function saveDraftToApi(state: OrgState): Promise<{ revision?: number; canonicalSavedAt?: string }> {
  try {
    const response = await fetch("/api/org/draft", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(state),
    });
    if (!response.ok) throw new Error(`Draft API failed: ${response.status}`);
    const payload = await response.json().catch(() => undefined) as { ok?: boolean; canonicalSavedAt?: string; revision?: number } | undefined;
    if (!payload?.ok) throw new Error("Draft API returned an invalid response");
    return { revision: payload.revision, canonicalSavedAt: payload.canonicalSavedAt };
  } catch {
    const draftPersisted = persistLocalDraft(state);
    const localVersion = saveLocalVersion("draft", state);
    if (!draftPersisted && !localVersion.persisted) throw new Error("Unable to persist draft locally");
    return { revision: localVersion.record.revision, canonicalSavedAt: localVersion.record.createdAt };
  }
}

async function publishToApi(state: OrgState): Promise<{ revision?: number; canonicalSavedAt?: string }> {
  try {
    const response = await fetch("/api/org/publish", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(state),
    });
    if (!response.ok) throw new Error(`Publish API failed: ${response.status}`);
    const payload = await response.json().catch(() => undefined) as { ok?: boolean; canonicalSavedAt?: string; revision?: number } | undefined;
    if (!payload?.ok) throw new Error("Publish API returned an invalid response");
    return { revision: payload.revision, canonicalSavedAt: payload.canonicalSavedAt };
  } catch {
    const draftPersisted = persistLocalDraft(state);
    const localVersion = saveLocalVersion("published", state);
    if (!draftPersisted && !localVersion.persisted) throw new Error("Unable to persist published version locally");
    return { revision: localVersion.record.revision, canonicalSavedAt: localVersion.record.createdAt };
  }
}
